// Quote line normalization vocabulary (P3-D) — the values QuoteLineItems.
// LineKind / MatchStatus / MatchSource may hold (guarded columns in db/71,
// written only by sp_SaveQuoteLineNormalization in db/73). The raw extracted
// line (Description / Quantity / Unit / UnitPrice / ExtendedPrice) is the
// evidence and is NEVER altered by normalization; these columns sit beside it.
//
//   LineKind     — the line's role in the vendor's offer (base scope prices
//                  like-for-like; everything else is compared separately)
//   MatchStatus  — the estimator's review state of the line's catalog identity
//                  (ItemID / AssemblyID). Only 'confirmed' lines level against
//                  each other; an AI / rule suggestion is never used silently.
//   MatchSource  — who proposed the identity

export const LINE_KINDS = [
  { key: "base",      label: "Base scope",  levels: true,  description: "Part of the vendor's base offer — compared like-for-like." },
  { key: "alternate", label: "Alternate",   levels: false, description: "Priced option outside the base — shown separately, never in the base comparison." },
  { key: "allowance", label: "Allowance",   levels: false, description: "Vendor allowance / plug number — shown separately." },
  { key: "exclusion", label: "Exclusion",   levels: false, description: "Scope the vendor excluded — listed so the gap is visible; no amount is compared." },
  { key: "freight",   label: "Freight",     levels: false, description: "Shipping / freight — commercial, compared separately." },
  { key: "tax",       label: "Tax",         levels: false, description: "Sales tax — commercial, compared separately." },
  { key: "other",     label: "Other",       levels: false, description: "Anything else (bond, mobilization, …)." },
];
export const LINE_KIND_KEYS = LINE_KINDS.map(k => k.key);
export const DEFAULT_LINE_KIND = "base";

export const MATCH_STATUSES = [
  { key: "unmatched", label: "Unmatched", description: "No catalog identity yet." },
  { key: "suggested", label: "Suggested", description: "A proposed Item / Assembly awaiting estimator confirmation — not used for leveling." },
  { key: "confirmed", label: "Confirmed", description: "Estimator-confirmed catalog identity — levels against other vendors' confirmed lines." },
  { key: "rejected",  label: "Rejected",  description: "The suggestion was wrong; identity cleared, kept as a record." },
];
export const MATCH_STATUS_KEYS = MATCH_STATUSES.map(s => s.key);
export const DEFAULT_MATCH_STATUS = "unmatched";

// ---- P3-E: estimator leveling adjustments (QuoteLevelingAdjustments, db/71 / db/74) --------------
// A plug stored BESIDE the quote so vendors compare like-for-like; the quoted
// amounts are never changed. Amount is signed dollars on the vendor's leveled number.
export const ADJUSTMENT_KINDS = [
  { key: "add_scope",    label: "Add excluded scope",   description: "The vendor excluded scope the others carry — add it at a documented price." },
  { key: "deduct_scope", label: "Deduct extra scope",   description: "The vendor included scope the others do not — take it out for comparison." },
  { key: "commercial",   label: "Commercial",           description: "Freight, tax, bond, terms — normalize to a common commercial basis." },
  { key: "quantity",     label: "Quantity correction",  description: "The vendor priced a different quantity — plug the difference." },
  { key: "other",        label: "Other",                description: "Anything else, explained in the note." },
];
export const ADJUSTMENT_KIND_KEYS = ADJUSTMENT_KINDS.map(k => k.key);
export const adjustmentKindLabel = (key) => ADJUSTMENT_KINDS.find(k => k.key === key)?.label || key || "—";
export const ADJUSTMENT_BASES = [
  { key: "other_vendor_line", label: "Another vendor's line", description: "Priced from a competing quote's line (named)." },
  { key: "estimate",          label: "Estimate",             description: "Priced from the estimate's own line for that scope." },
  { key: "catalog",           label: "Catalog cost",         description: "Priced from the catalog Item / Assembly cost." },
  { key: "estimator",         label: "Estimator judgement",  description: "An estimator figure — explain it in the note." },
];
export const ADJUSTMENT_BASIS_KEYS = ADJUSTMENT_BASES.map(b => b.key);
export const adjustmentBasisLabel = (key) => ADJUSTMENT_BASES.find(b => b.key === key)?.label || key || "—";
